/**
 * Viewer de stream de tela remota (WebRTC)
 * Exibe o vídeo do cliente no chat do atendente
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { useRef, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Maximize2, 
  Minimize2, 
  X, 
  Signal, 
  SignalLow, 
  SignalZero,
  Monitor,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ScreenShareSessionState } from '@/hooks/webrtc/types';

interface ScreenShareViewerProps {
  /** Stream de vídeo remoto */
  stream: MediaStream | null;
  /** Estado da sessão */
  sessionState: ScreenShareSessionState;
  /** Estado da conexão P2P */
  connectionState: RTCPeerConnectionState;
  /** Callback para encerrar sessão */
  onClose: () => void;
  /** Callback para expandir em tela cheia (modal) */
  onExpand?: () => void;
  /** Nome do contato/cliente */
  contactName?: string;
  /** Classes CSS adicionais */
  className?: string;
}

/**
 * Componente viewer para exibir stream de tela remota
 * 
 * Exibido como um painel flutuante sobre a área de mensagens do chat.
 * Inclui controles para:
 * - Encerrar sessão
 * - Expandir para fullscreen (modal)
 * - Indicador de qualidade da conexão
 * - Timer da duração da sessão
 */
export function ScreenShareViewer({
  stream,
  sessionState,
  connectionState,
  onClose,
  onExpand,
  contactName = 'Cliente',
  className,
}: ScreenShareViewerProps) {
  // Ref para o elemento de vídeo
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // Estado para duração da sessão
  const [duration, setDuration] = useState(0);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Conecta stream ao elemento de vídeo
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }

    return () => {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [stream]);

  // Timer de duração quando conectado
  useEffect(() => {
    if (sessionState === 'connected') {
      setDuration(0);
      durationIntervalRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    } else {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
    }

    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, [sessionState]);

  // Formata duração em MM:SS
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Ícone de qualidade da conexão baseado no estado
  const getConnectionIcon = () => {
    switch (connectionState) {
      case 'connected':
        return <Signal className="h-3 w-3 text-green-500" />;
      case 'connecting':
        return <SignalLow className="h-3 w-3 text-yellow-500" />;
      case 'failed':
      case 'disconnected':
        return <SignalZero className="h-3 w-3 text-red-500" />;
      default:
        return <SignalLow className="h-3 w-3 text-muted-foreground" />;
    }
  };

  // Se não está conectado ou conectando, não exibe
  if (sessionState !== 'connected' && sessionState !== 'connecting') {
    return null;
  }

  return (
    <div
      className={cn(
        'absolute inset-x-4 top-4 z-50',
        'bg-card rounded-lg border shadow-lg overflow-hidden',
        'flex flex-col',
        className
      )}
      style={{ maxHeight: 'calc(100% - 140px)' }}
    >
      {/* Header do viewer */}
      <div className="flex items-center justify-between px-3 py-2 bg-muted/50 border-b">
        <div className="flex items-center gap-2">
          <Monitor className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium truncate max-w-[120px]">
            {contactName}
          </span>
          
          {/* Badge de status */}
          <Badge 
            variant={connectionState === 'connected' ? 'default' : 'secondary'}
            className="text-xs py-0 h-5"
          >
            {getConnectionIcon()}
            <span className="ml-1">
              {connectionState === 'connected' ? 'Ao vivo' : 'Conectando...'}
            </span>
          </Badge>
          
          {/* Timer de duração */}
          {sessionState === 'connected' && (
            <span className="text-xs text-muted-foreground font-mono">
              {formatDuration(duration)}
            </span>
          )}
        </div>

        {/* Controles */}
        <div className="flex items-center gap-1">
          {/* Botão expandir */}
          {onExpand && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onExpand}
              title="Expandir"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
          )}
          
          {/* Botão fechar */}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive hover:text-destructive"
            onClick={onClose}
            title="Encerrar visualização"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Área do vídeo */}
      <div className="relative flex-1 bg-black min-h-[200px]">
        {sessionState === 'connecting' ? (
          // Loading state
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white/70">
            <Loader2 className="h-8 w-8 animate-spin mb-2" />
            <span className="text-sm">Estabelecendo conexão...</span>
          </div>
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-contain"
          />
        )}
      </div>
    </div>
  );
}
